import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { 
  Filter, 
  ChevronDown, 
  ChevronRight,
  Car, 
  Users, 
  Wrench, 
  Fuel, 
  DollarSign, 
  FileText, 
  AlertTriangle,
  MapPin,
  CalendarIcon,
  RotateCcw,
  Check,
  SlidersHorizontal
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { ConsolidatedFilters } from "@/hooks/use-consolidated-report";

interface FiltersSheetProps {
  filters: ConsolidatedFilters;
  onFilterChange: (filters: ConsolidatedFilters) => void;
  onApply: () => void;
  onReset: () => void;
  trigger?: React.ReactNode;
}

const FILTER_SECTIONS = [
  { key: 'vehicles' as const, label: 'Veículos', icon: Car, color: 'text-blue-500', description: 'Frota completa e status' },
  { key: 'drivers' as const, label: 'Motoristas', icon: Users, color: 'text-green-500', description: 'Quadro de motoristas' },
  { key: 'maintenance' as const, label: 'Manutenção', icon: Wrench, color: 'text-orange-500', description: 'OS e planos preventivos' },
  { key: 'fuel' as const, label: 'Combustível', icon: Fuel, color: 'text-purple-500', description: 'Abastecimentos e consumo' },
  { key: 'costs' as const, label: 'Custos', icon: DollarSign, color: 'text-emerald-500', description: 'Despesas e investimentos' },
  { key: 'documents' as const, label: 'Documentos', icon: FileText, color: 'text-cyan-500', description: 'Licenças e certificados' },
  { key: 'alerts' as const, label: 'Alertas', icon: AlertTriangle, color: 'text-red-500', description: 'Notificações pendentes' },
  { key: 'trips' as const, label: 'Viagens', icon: MapPin, color: 'text-indigo-500', description: 'Rotas e deslocamentos' },
];

export function FiltersSheet({ 
  filters, 
  onFilterChange, 
  onApply, 
  onReset,
  trigger
}: FiltersSheetProps) {
  const [open, setOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({
    modules: true,
    period: true
  });

  const activeFiltersCount = FILTER_SECTIONS.filter(s => filters[s.key]).length;

  const toggleFilter = (key: keyof ConsolidatedFilters) => {
    if (typeof filters[key] === 'boolean') {
      onFilterChange({ ...filters, [key]: !filters[key] });
    }
  };

  const toggleAllFilters = (enabled: boolean) => {
    const newFilters = { ...filters };
    FILTER_SECTIONS.forEach(section => {
      newFilters[section.key] = enabled;
    });
    onFilterChange(newFilters);
  };

  const setDateRange = (range: { from?: Date; to?: Date }) => {
    onFilterChange({ ...filters, dateRange: range });
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            Filtros
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
        <SheetHeader className="p-6 pb-4 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="flex items-center gap-2 text-lg">
                <Filter className="h-5 w-5 text-primary" />
                Filtros do Relatório
              </SheetTitle>
              <SheetDescription className="mt-1">
                Personalize os dados incluídos no relatório
              </SheetDescription>
            </div>
            <Badge variant="outline" className="font-mono text-sm px-3 py-1">
              {activeFiltersCount}/{FILTER_SECTIONS.length} ativos
            </Badge>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-220px)]">
          <div className="p-6 space-y-6">
            {/* Quick Actions */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => toggleAllFilters(true)}
              >
                <Check className="h-4 w-4 mr-2" />
                Selecionar Todos
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => toggleAllFilters(false)}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Limpar Seleção
              </Button>
            </div>

            <Separator />

            {/* Module Filters */}
            <Collapsible 
              open={sectionsOpen.modules} 
              onOpenChange={(open) => setSectionsOpen(prev => ({ ...prev, modules: open }))}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-semibold hover:text-primary transition-colors">
                <span className="text-base">Módulos do Relatório</span>
                {sectionsOpen.modules ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-3">
                {FILTER_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const isActive = filters[section.key];
                  
                  return (
                    <div
                      key={section.key}
                      className={cn(
                        "flex items-center justify-between p-4 rounded-xl transition-all cursor-pointer",
                        isActive 
                          ? "bg-primary/10 border-2 border-primary/30 shadow-sm" 
                          : "bg-muted/50 border-2 border-transparent hover:bg-muted hover:border-muted-foreground/20"
                      )}
                      onClick={() => toggleFilter(section.key)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          isActive ? "bg-background" : "bg-background/60"
                        )}>
                          <Icon className={cn("h-5 w-5", section.color)} />
                        </div>
                        <div>
                          <Label 
                            htmlFor={section.key} 
                            className="text-sm font-medium cursor-pointer"
                          >
                            {section.label}
                          </Label>
                          <p className="text-xs text-muted-foreground">{section.description}</p>
                        </div>
                      </div>
                      <Switch
                        id={section.key}
                        checked={isActive}
                        onCheckedChange={() => toggleFilter(section.key)}
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>

            <Separator />

            {/* Date Range */}
            <Collapsible 
              open={sectionsOpen.period} 
              onOpenChange={(open) => setSectionsOpen(prev => ({ ...prev, period: open }))}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-semibold hover:text-primary transition-colors">
                <span className="text-base">Período de Análise</span>
                {sectionsOpen.period ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 space-y-4">
                <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-12",
                        !filters.dateRange?.from && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-3 h-5 w-5" />
                      {filters.dateRange?.from ? (
                        filters.dateRange.to ? (
                          <span className="font-medium">
                            {format(filters.dateRange.from, "dd MMM yyyy", { locale: ptBR })} → {" "}
                            {format(filters.dateRange.to, "dd MMM yyyy", { locale: ptBR })}
                          </span>
                        ) : (
                          format(filters.dateRange.from, "dd MMMM yyyy", { locale: ptBR })
                        )
                      ) : (
                        <span>Selecionar período personalizado</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={filters.dateRange?.from}
                      selected={{
                        from: filters.dateRange?.from,
                        to: filters.dateRange?.to
                      }}
                      onSelect={(range) => {
                        setDateRange({ from: range?.from, to: range?.to });
                      }}
                      numberOfMonths={2}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>

                {/* Quick Period Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), now.getMonth(), 1);
                      setDateRange({ from: start, to: now });
                    }}
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Este mês
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                      setDateRange({ from: start, to: now });
                    }}
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Últimos 90 dias
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), 0, 1);
                      setDateRange({ from: start, to: now });
                    }}
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Este ano
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    onClick={() => setDateRange({ from: undefined, to: undefined })}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Limpar período
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ScrollArea>

        {/* Action Buttons */}
        <div className="p-6 border-t bg-muted/30 space-y-3">
          <Button 
            className="w-full h-12 text-base font-semibold" 
            onClick={handleApply}
            disabled={activeFiltersCount === 0}
          >
            <Check className="h-5 w-5 mr-2" />
            Aplicar Filtros
          </Button>
          <Button 
            variant="ghost" 
            className="w-full text-muted-foreground" 
            onClick={onReset}
          >
            Restaurar Configurações Padrão
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
